import api from './client';
import type { Vehicle, VehicleReservation } from 'types/garage/vehicules';

// Backend uses snake_case, frontend uses camelCase for reservations
interface ApiReservation {
  id: string;
  org_id: string;
  vehicle_id: string;
  client_name: string;
  start_date: string;
  end_date: string;
  has_pickup: boolean;
  has_return: boolean;
  start_hour?: number;
  duration_hours?: number;
  return_hour?: number;
  ordre_id?: string;
  dossier_id?: string;
  notes?: string;
  client_email?: string;
  client_phone?: string;
  km_depart?: string;
  km_retour?: string;
  servicebox_dossier_id?: string;
  servicebox_pret_id?: string;
}

function toFrontendReservation(r: ApiReservation): VehicleReservation {
  return {
    id: r.id,
    vehicleId: r.vehicle_id,
    clientName: r.client_name,
    startDate: r.start_date,
    endDate: r.end_date,
    hasPickup: r.has_pickup,
    hasReturn: r.has_return,
    startHour: r.start_hour,
    durationHours: r.duration_hours,
    returnHour: r.return_hour,
    ordreId: r.ordre_id,
    dossierId: r.dossier_id,
    notes: r.notes,
    clientEmail: r.client_email,
    clientPhone: r.client_phone,
    kmDepart: r.km_depart,
    kmRetour: r.km_retour,
    serviceboxDossierId: r.servicebox_dossier_id,
    serviceboxPretId: r.servicebox_pret_id,
  };
}

function toApiReservation(r: Partial<VehicleReservation> & { org_id?: string }): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (r.vehicleId !== undefined) out.vehicle_id = r.vehicleId;
  if (r.clientName !== undefined) out.client_name = r.clientName;
  if (r.startDate !== undefined) out.start_date = r.startDate;
  if (r.endDate !== undefined) out.end_date = r.endDate;
  if (r.hasPickup !== undefined) out.has_pickup = r.hasPickup;
  if (r.hasReturn !== undefined) out.has_return = r.hasReturn;
  if (r.startHour !== undefined) out.start_hour = r.startHour;
  if (r.durationHours !== undefined) out.duration_hours = r.durationHours;
  if (r.returnHour !== undefined) out.return_hour = r.returnHour;
  if (r.ordreId !== undefined) out.ordre_id = r.ordreId;
  if (r.dossierId !== undefined) out.dossier_id = r.dossierId;
  if (r.notes !== undefined) out.notes = r.notes;
  if (r.clientEmail !== undefined) out.client_email = r.clientEmail;
  if (r.clientPhone !== undefined) out.client_phone = r.clientPhone;
  if (r.kmDepart !== undefined) out.km_depart = r.kmDepart;
  if (r.kmRetour !== undefined) out.km_retour = r.kmRetour;
  if (r.serviceboxDossierId !== undefined) out.servicebox_dossier_id = r.serviceboxDossierId;
  if (r.serviceboxPretId !== undefined) out.servicebox_pret_id = r.serviceboxPretId;
  if ('org_id' in r) out.org_id = r.org_id;
  return out;
}

/** Ordre dont le client a demandé une mobilité sans en avoir (GET /vehicles/mobility-requests/unassigned). */
export interface UnassignedMobilityRequest {
  ordre_id: string;
  num?: string | null;
  name?: string | null;
  immatriculation?: string | null;
  modele?: string | null;
  mobility_option?: string | null;
  usage?: string | null;
  reception_ts: number | null;
  restitution_ts: number | null;
  reason: "no_reservation" | "vehicle_deleted";
}

export const vehiclesApi = {
  unassignedMobilityRequests: async (orgId: string): Promise<UnassignedMobilityRequest[]> => {
    const { data } = await api.get('/vehicles/mobility-requests/unassigned', { params: { org_id: orgId } });
    return data;
  },

  list: async (params?: { org_id?: string; category?: string; usage?: string; status?: string; limit?: number; offset?: number; sort_by?: string; sort_dir?: string }): Promise<Vehicle[]> => {
    const { data } = await api.get('/vehicles', { params });
    return data;
  },

  get: async (id: string): Promise<Vehicle> => {
    const { data } = await api.get(`/vehicles/${id}`);
    return data;
  },

  create: async (vehicle: Omit<Vehicle, 'id'>): Promise<Vehicle> => {
    const { data } = await api.post('/vehicles', vehicle);
    return data;
  },

  update: async (id: string, updates: Partial<Vehicle>): Promise<Vehicle> => {
    const { data } = await api.patch(`/vehicles/${id}`, updates);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/vehicles/${id}`);
  },

  /** Free fleet vehicles over a window. Location vehicles whose class the garage
   *  excluded (Réglages → Prise de RDV) are filtered out unless `all_categories`.
   *  `exclude_reservation_id` ignore une réservation — celle qu'on modifie, qui sinon
   *  rendrait son propre véhicule indisponible. */
  available: async (params: { org_id: string; start_date: string; end_date: string; usage?: string; start_hour?: number; end_hour?: number; all_categories?: boolean; exclude_reservation_id?: string }): Promise<Vehicle[]> => {
    const { data } = await api.get('/vehicles/availability', { params });
    return data;
  },

  // Reservations
  /** `ordre_id` renvoie TOUTES les mobilités d'un ordre : un prêt puis une location qui
   *  se suivent sont deux réservations, l'ordre n'en reflète qu'une (la courante). */
  listReservations: async (params?: { org_id?: string; vehicle_id?: string; ordre_id?: string; limit?: number; offset?: number }): Promise<VehicleReservation[]> => {
    const { data } = await api.get('/vehicles/reservations/list', { params });
    return (data as ApiReservation[]).map(toFrontendReservation);
  },

  getReservation: async (id: string): Promise<VehicleReservation> => {
    const { data } = await api.get(`/vehicles/reservations/${id}`);
    return toFrontendReservation(data);
  },

  createReservation: async (reservation: Omit<VehicleReservation, 'id'> & { org_id: string }): Promise<VehicleReservation> => {
    const { data } = await api.post('/vehicles/reservations', toApiReservation(reservation));
    return toFrontendReservation(data);
  },

  updateReservation: async (id: string, updates: Partial<VehicleReservation>): Promise<VehicleReservation> => {
    const { data } = await api.patch(`/vehicles/reservations/${id}`, toApiReservation(updates));
    return toFrontendReservation(data);
  },

  /** Changer de véhicule en cours de mobilité : l'actuelle rentre à end_*, une nouvelle
   *  part à start_* (défaut : le même instant). Renvoie [ancienne, nouvelle]. */
  swapReservation: async (id: string, body: { vehicle_id: string; end_date: string; end_hour: number; start_date?: string; start_hour?: number }): Promise<VehicleReservation[]> => {
    const { data } = await api.post(`/vehicles/reservations/${id}/swap`, body);
    return (data as ApiReservation[]).map(toFrontendReservation);
  },

  deleteReservation: async (id: string): Promise<void> => {
    await api.delete(`/vehicles/reservations/${id}`);
  },

  // Vehicle Models (dynamic marque/modele catalog)
  listModels: async (org_id: string): Promise<{ id: string; org_id: string; marque: string; modele: string }[]> => {
    const { data } = await api.get('/vehicles/models', { params: { org_id } });
    return data;
  },

  createModel: async (org_id: string, marque: string, modele: string): Promise<{ id: string; org_id: string; marque: string; modele: string }> => {
    const { data } = await api.post('/vehicles/models', { org_id, marque, modele });
    return data;
  },
};
